import { Controller } from "@hotwired/stimulus";

// Right sidebar mode switcher for Custom Listing tool.
// Modes (extensible): list, edit, favorites, programs
export default class extends Controller {
  static targets = ["panel", "catalogTab", "simulationTab", "favoritesTab"];
  static values = {
    mode: { type: String, default: "list" },
    catalogAccessible: { type: Boolean, default: false },
  };

  connect() {
    this.applyMode();
  }

  // Appelé par `profiles_controller.js` au chargement d'un profil sans
  // rechargement (#305) : rebascule sur l'onglet « Catalogue » (mode `list`).
  // Cas limite : si l'utilisateur n'a pas accès au catalogue (mode initial
  // `edit`), on ne force rien et on conserve le comportement par défaut.
  showListForProfileLoad() {
    if (!this.catalogAccessibleValue) return;
    this.showList();
  }

  showList(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    this.setMode("list");
    // Reset the fiscality sticky patch when returning to list, so the profile
    // select becomes the source of truth again. See docs/fiscality_selector.md.
    this.emitClearStickyPatch();
    this.emitListShown();
  }

  showSimulation(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    this.setMode("edit");
  }

  emitClearStickyPatch() {
    try {
      this.element.dispatchEvent(new CustomEvent("api-sync:clear-sticky-patch", {
        bubbles: true,
        detail: { scope: "listing_details" }
      }));
    } catch (_) {}
  }

  emitListShown() {
    try {
      this.element.dispatchEvent(new CustomEvent("listing-browser:list-shown", { bubbles: true }));
    } catch (_) {}
  }

  showEdit(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    this.setMode("edit");
  }

  showFavorites(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    this.setMode("favorites");
  }

  showPrograms(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    this.setMode("programs");
  }

  setMode(mode) {
    this.modeValue = String(mode || "list");
    this.applyMode();
  }

  applyMode() {
    const current = this.modeValue || "list";
    this.panelTargets.forEach((panel) => {
      const mode = panel.dataset.mode || "";
      panel.classList.toggle("d-none", mode !== current);
    });
    // Update tab active states
    this.updateTabStates(current);
    // Propage le mode au conteneur racine pour que d'autres parties de l'UI
    // (CSS, autres contrôleurs) puissent réagir au changement Catalogue /
    // Simulation libre. Cf. issue #173 (image du bien masquée en simulation
    // libre via une règle CSS sur `[data-simulator-mode]`).
    this.updateRootMode(current);
  }

  updateRootMode(currentMode) {
    const root = this.element.closest(".simulator-panel");
    if (!root) return;
    root.dataset.simulatorMode = currentMode;
  }

  updateTabStates(currentMode) {
    // Catalog tab is active when mode is "list"
    if (this.hasCatalogTabTarget) {
      this.catalogTabTarget.classList.toggle("active", currentMode === "list");
    }
    // Simulation tab is active when mode is "edit"
    if (this.hasSimulationTabTarget) {
      this.simulationTabTarget.classList.toggle("active", currentMode === "edit");
    }
    // Favorites tab is active when mode is "favorites"
    if (this.hasFavoritesTabTarget) {
      this.favoritesTabTarget.classList.toggle("active", currentMode === "favorites");
    }
  }
}

